import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch, NetworkError } from '../driver/sync/api'
import { card, color, container, h1, muted, page, TAP } from '../driver/ui'
import { getWalkMeta, setWalkMeta } from './walkQueue'
import type { WalkSubscriberRow } from './types'

const STEP_LABEL: Record<string, string> = {
  email_verified: 'email not verified',
  password_set: 'no card link used yet',
  animals: 'no animals',
  bins: 'no bins',
  delivery_preferences: 'no delivery day',
  property_capture: 'property not captured',
  card: 'no card',
}

/** Who can be walked. The list is kept on the phone, so it opens with no signal. */
export default function WalkList({ offline }: { offline: boolean }) {
  const [rows, setRows] = useState<WalkSubscriberRow[] | null>(null)
  const [q, setQ] = useState('')
  const [stale, setStale] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    const handle = window.setTimeout(async () => {
      try {
        const res = await apiFetch(`/delivery/walk/subscribers/?q=${encodeURIComponent(q.trim())}`)
        const body = await res.json().catch(() => null)
        if (!alive) return
        if (!res.ok) {
          setError(body?.error || `Could not load customers (${res.status}).`)
          return
        }
        setRows(body.subscribers)
        setStale(false)
        setError('')
        if (!q.trim()) await setWalkMeta('subscribers', body.subscribers)
      } catch (err) {
        if (!alive) return
        if (err instanceof NetworkError) {
          const cached = await getWalkMeta<WalkSubscriberRow[]>('subscribers')
          const needle = q.trim().toLowerCase()
          setRows((cached || []).filter((r) => !needle || `${r.name} ${r.service_address} ${r.city}`.toLowerCase().includes(needle)))
          setStale(true)
        } else {
          setError('Could not load customers.')
        }
      }
    }, 250)
    return () => {
      alive = false
      window.clearTimeout(handle)
    }
  }, [q])

  return (
    <div style={page}>
      <div style={container}>
        <div style={h1}>Property walk</div>
        <div style={{ ...muted, marginBottom: 12 }}>
          Walk the property with the customer. They add their own card from a link we email them.
        </div>
        {(offline || stale) && (
          <div style={{ ...card, background: '#fffbeb', borderColor: color.amber }}>
            No signal. Showing the customers saved on this phone.
          </div>
        )}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or address"
          style={{ width: '100%', boxSizing: 'border-box', minHeight: TAP, padding: '0 14px', fontSize: 18,
            border: `1px solid ${color.greyLight}`, borderRadius: 12, marginBottom: 12 }}
        />
        {error && <div style={{ color: color.red, marginBottom: 12 }}>{error}</div>}
        {rows === null && !error && <div style={muted}>Loading…</div>}
        {rows !== null && rows.length === 0 && <div style={muted}>No customers match.</div>}
        {(rows || []).map((r) => (
          <Link key={r.id} to={`/delivery/walk/${r.id}`} style={{ ...card, display: 'block', color: color.ink, textDecoration: 'none' }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{r.name}</div>
            <div style={muted}>{r.service_address}, {r.city}</div>
            <div style={{ fontSize: 14, marginTop: 6, color: r.surveyed_at ? color.greenDark : color.amber, fontWeight: 700 }}>
              {r.surveyed_at ? 'Walked' : 'Not walked yet'}
              {r.next_step ? ` · ${STEP_LABEL[r.next_step] || r.next_step}` : ' · setup complete'}
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
